import { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, CheckCircle2, Circle, MinusCircle, RefreshCw } from "lucide-react";

/**
 * PrerequisitesChecklistPanel — Checklist des 4 prérequis avant le Gala.
 * Chaque prérequis a un compteur current/target (0-4), un statut et des notes.
 */
export default function PrerequisitesChecklistPanel({ API }) {
  const pin = "SAFUAYE2024";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/governance/prerequisites`, { params: { pin } });
      setData(r.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const update = async (key, patch) => {
    setBusy(key);
    try {
      await axios.put(`${API}/governance/prerequisites/${key}`, { pin, ...patch });
      load();
    } catch (e) { alert("❌ " + (e.response?.data?.detail || e.message)); }
    setBusy("");
  };

  if (!data) return <div className="p-4 text-sm text-gray-500">Chargement...</div>;

  const iconFor = (status) => status === "done"
    ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
    : status === "in_progress"
    ? <MinusCircle className="w-5 h-5 text-amber-500" />
    : <Circle className="w-5 h-5 text-slate-400" />;

  return (
    <div className="bg-white rounded-xl p-5 shadow-lg mt-6" data-testid="prereq-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-amber-700" />
          Prérequis avant Gala — Suivi en temps réel
        </h4>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs bg-slate-200 hover:bg-slate-300 px-2 py-1 rounded">
          <RefreshCw className="w-3 h-3" /> Rafraîchir
        </button>
      </div>

      {/* Global progress */}
      <div className={`rounded-lg p-3 mb-3 ${data.gala_ready ? "bg-emerald-50 border border-emerald-300" : "bg-amber-50 border border-amber-300"}`}
           data-testid="prereq-global">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-bold text-gray-900">
            {data.gala_ready ? "✅ Gala prêt à être planifié" : `🚧 ${data.total_done}/${data.total_target} éléments validés — Gala en attente`}
          </p>
          <span className="text-xs font-mono">{data.progress_pct}%</span>
        </div>
        <div className="w-full bg-white rounded-full h-2 overflow-hidden">
          <div className={`h-2 ${data.gala_ready ? "bg-emerald-600" : "bg-amber-500"}`}
               style={{ width: `${data.progress_pct}%` }} />
        </div>
      </div>

      {/* 4 items */}
      <div className="space-y-2">
        {data.items.map((p) => (
          <div key={p.key}
               className={`border rounded-lg p-3 flex flex-col gap-2 ${p.status === "done" ? "bg-emerald-50 border-emerald-300" : p.status === "in_progress" ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}
               data-testid={`prereq-${p.key}`}>
            <div className="flex items-start gap-2">
              {iconFor(p.status)}
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-900">{p.label_fr}</p>
                <p className="text-[11px] text-gray-600">
                  <strong>Responsable :</strong> {p.owner}
                  {p.notes && <> · <em>{p.notes}</em></>}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => update(p.key, { current_count: Math.max(0, (p.current_count || 0) - 1) })}
                        disabled={busy === p.key || (p.current_count || 0) <= 0}
                        data-testid={`prereq-${p.key}-dec`}
                        className="w-7 h-7 bg-white border rounded hover:bg-slate-100 disabled:opacity-40 text-sm font-bold">−</button>
                <span className="font-mono text-sm font-bold w-14 text-center" data-testid={`prereq-${p.key}-count`}>
                  {p.current_count || 0} / {p.target_count}
                </span>
                <button onClick={() => update(p.key, { current_count: Math.min(p.target_count, (p.current_count || 0) + 1) })}
                        disabled={busy === p.key || (p.current_count || 0) >= p.target_count}
                        data-testid={`prereq-${p.key}-inc`}
                        className="w-7 h-7 bg-white border rounded hover:bg-slate-100 disabled:opacity-40 text-sm font-bold">+</button>
              </div>
            </div>
            {p.updated_at && (
              <p className="text-[10px] text-gray-400 self-end">
                MàJ : {new Date(p.updated_at).toLocaleString("fr-FR")}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
